import { z } from 'zod';
import { DocumentTypeSchema } from '../catalogs/domain-catalogs.js';
import { TimestampSchema } from '../common/clock.js';
import { ConsoleUserIdSchema, CustomerIdSchema, PasskeyIdSchema, ScaChallengeIdSchema, } from '../common/ids.js';
import { MoneySchema } from '../common/money.js';
import { TenantCodeSchema } from '../common/tenant.js';
/** Scopes (`04-api-contratos.md` §2). */
export const CUSTOMER_SCOPES = [
    'customer:read',
    'payments:write',
    'origination:write',
    'agents:chat',
];
export const BANK_SCOPES = [
    'bank:customers',
    'bank:payments.read',
    'bank:disputes.decide',
    'bank:webhooks',
];
export const ADMIN_SCOPES = [
    'admin:origination',
    'admin:payments',
    'admin:knowledge',
    'admin:config',
    'admin:audit',
    'admin:demo',
];
/** Servicio a servicio (api → credit-core / sandbox). */
export const SERVICE_SCOPES = ['credit-core', 'sandbox'];
export const SCOPES = [
    ...CUSTOMER_SCOPES,
    ...BANK_SCOPES,
    ...ADMIN_SCOPES,
    ...SERVICE_SCOPES,
];
export const ScopeSchema = z.enum(SCOPES).meta({ id: 'Scope' });
/** Roles de consola (RBAC) → scopes. */
export const CONSOLE_ROLES = [
    'ADMIN',
    'CREDIT_ANALYST',
    'PAYMENTS_OPERATOR',
    'KB_ADMIN',
    'AUDITOR',
    'RISK',
];
export const ConsoleRoleSchema = z.enum(CONSOLE_ROLES).meta({ id: 'ConsoleRole' });
export const ROLE_SCOPES = {
    ADMIN: ADMIN_SCOPES,
    CREDIT_ANALYST: ['admin:origination'],
    PAYMENTS_OPERATOR: ['admin:payments'],
    KB_ADMIN: ['admin:knowledge'],
    AUDITOR: ['admin:audit'],
    RISK: ['admin:origination', 'admin:payments'],
};
export const TOKEN_TYPES = ['customer', 'widget_session', 'console', 'client', 'service'];
export const TokenTypeSchema = z.enum(TOKEN_TYPES);
/** Vida de los tokens (segundos). */
export const TOKEN_TTL_SECONDS = {
    customer: 15 * 60,
    customerRefresh: 30 * 24 * 60 * 60,
    widgetSessionMin: 5 * 60,
    widgetSessionMax: 15 * 60,
    console: 8 * 60 * 60,
    client: 60 * 60,
    sca: 5 * 60,
};
/** Claims del JWT de Paguz (emitido y verificado por `server-kit` con jose). */
export const AccessTokenClaimsSchema = z
    .object({
    iss: z.string(),
    aud: z.union([z.string(), z.array(z.string())]),
    sub: z.string(),
    exp: z.int(),
    iat: z.int(),
    jti: z.string().optional(),
    typ: TokenTypeSchema,
    tenantId: z.uuid(),
    tenantCode: TenantCodeSchema,
    scopes: z.array(ScopeSchema),
    /** Solo widget_session: orígenes permitidos. */
    allowedOrigins: z.array(z.string()).optional(),
    roles: z.array(ConsoleRoleSchema).optional(),
})
    .meta({ id: 'AccessTokenClaims' });
// ── Requests/responses de auth ────────────────────────────────────────────
export const PhoneSchema = z
    .string()
    .regex(/^\+573\d{9}$/, 'Celular colombiano en formato +573XXXXXXXXX')
    .meta({ id: 'Phone', examples: ['+573001234567'] });
export const OtpPurposeSchema = z.enum(['LOGIN', 'SIGNUP', 'REGISTER', 'CONSENT', 'SIGN']);
export const OtpStartRequestSchema = z
    .object({ phone: PhoneSchema, purpose: z.enum(['LOGIN', 'SIGNUP']) })
    .meta({ id: 'OtpStartRequest' });
export const OtpStartResponseSchema = z
    .object({ challengeId: z.string(), expiresAt: TimestampSchema, demoCode: z.string().optional() })
    .meta({ id: 'OtpStartResponse' });
export const SignupDataSchema = z.object({
    fullName: z.string().trim().min(3).max(120),
    documentType: DocumentTypeSchema,
    documentNumber: z.string().regex(/^[0-9A-Z]{4,15}$/),
    /** Nombre del negocio del microempresario (opcional). */
    businessName: z.string().trim().min(2).max(120).optional(),
    acceptTerms: z.literal(true),
    acceptHabeasData: z.literal(true),
    termsVersion: z.string(),
});
export const OtpVerifyRequestSchema = z
    .object({
    challengeId: z.string(),
    code: z.string().regex(/^\d{6}$/),
    signupData: SignupDataSchema.optional(),
})
    .meta({ id: 'OtpVerifyRequest' });
export const TokenPairSchema = z.object({ accessToken: z.string(), refreshToken: z.string() });
export const RefreshRequestSchema = z.object({ refreshToken: z.string() });
export const ConsoleLoginRequestSchema = z
    .object({ email: z.email(), password: z.string().min(8) })
    .meta({ id: 'ConsoleLoginRequest' });
export const ConsoleUserSchema = z
    .object({
    id: ConsoleUserIdSchema,
    email: z.email(),
    fullName: z.string(),
    roles: z.array(ConsoleRoleSchema),
})
    .meta({ id: 'ConsoleUser' });
export const ConsoleLoginResponseSchema = z.object({
    accessToken: z.string(),
    user: ConsoleUserSchema,
});
/** RFC 6749 client_credentials (campos snake_case por estándar). */
export const OAuthTokenRequestSchema = z.object({
    grant_type: z.literal('client_credentials'),
    scope: z.string().optional(),
});
export const OAuthTokenResponseSchema = z.object({
    access_token: z.string(),
    token_type: z.literal('Bearer'),
    expires_in: z.int(),
    scope: z.string(),
});
export const CreateWidgetSessionRequestSchema = z
    .object({
    customerRef: z.string().min(4),
    scopes: z.array(z.enum(CUSTOMER_SCOPES)).min(1),
    ttlSeconds: z
        .int()
        .min(TOKEN_TTL_SECONDS.widgetSessionMin)
        .max(TOKEN_TTL_SECONDS.widgetSessionMax),
    allowedOrigins: z.array(z.url()).min(1),
})
    .meta({ id: 'CreateWidgetSessionRequest' });
export const CreateWidgetSessionResponseSchema = z
    .object({
    widgetSessionToken: z.string(),
    expiresAt: TimestampSchema,
    customerId: CustomerIdSchema,
})
    .meta({ id: 'CreateWidgetSessionResponse' });
// ── SCA (autenticación fuerte, dynamic linking) ───────────────────────────
export const ScaOperationTypeSchema = z
    .enum(['PAYMENT_INTENT', 'CREDIT_PAYMENT', 'DISPUTE', 'OFFER_ACCEPTANCE', 'ESIGN'])
    .meta({ id: 'ScaOperationType' });
export const ScaMethodSchema = z.enum(['OTP', 'PASSKEY', 'DELEGATED']).meta({ id: 'ScaMethod' });
export const ScaChallengeStatusSchema = z.enum([
    'PENDING',
    'VERIFIED',
    'FAILED',
    'EXPIRED',
    'CONSUMED',
]);
export const CreateScaChallengeRequestSchema = z
    .object({
    operationType: ScaOperationTypeSchema,
    operationId: z.string(),
    method: ScaMethodSchema,
})
    .meta({ id: 'CreateScaChallengeRequest' });
export const ScaChallengeSchema = z
    .object({
    challengeId: ScaChallengeIdSchema,
    expiresAt: TimestampSchema,
    method: ScaMethodSchema,
    demoCode: z.string().optional(),
})
    .meta({ id: 'ScaChallenge' });
// ── Passkeys (WebAuthn de plataforma: huella, Face ID, Windows Hello) ─────
/** base64url sin relleno (WebAuthn JSON). */
const Base64UrlSchema = z.string().regex(/^[A-Za-z0-9_-]*$/);
const AuthenticatorTransportSchema = z.string().max(32);
const CredentialDescriptorSchema = z.looseObject({
    id: Base64UrlSchema,
    type: z.literal('public-key'),
    transports: z.array(AuthenticatorTransportSchema).optional(),
});
/** `PublicKeyCredentialCreationOptionsJSON` (WebAuthn L3) para `navigator.credentials.create`. */
export const PasskeyRegistrationOptionsSchema = z
    .looseObject({
    challenge: Base64UrlSchema,
    rp: z.looseObject({ id: z.string().optional(), name: z.string() }),
    user: z.looseObject({ id: Base64UrlSchema, name: z.string(), displayName: z.string() }),
    pubKeyCredParams: z.array(z.looseObject({ alg: z.number().int(), type: z.literal('public-key') })),
    timeout: z.number().int().optional(),
    excludeCredentials: z.array(CredentialDescriptorSchema).optional(),
    authenticatorSelection: z.record(z.string(), z.unknown()).optional(),
    attestation: z.string().optional(),
})
    .meta({ id: 'PasskeyRegistrationOptions' });
/** `PublicKeyCredentialRequestOptionsJSON` para `navigator.credentials.get`. */
export const PasskeyAuthenticationOptionsSchema = z
    .looseObject({
    challenge: Base64UrlSchema,
    rpId: z.string().optional(),
    timeout: z.number().int().optional(),
    allowCredentials: z.array(CredentialDescriptorSchema).optional(),
    userVerification: z.enum(['required', 'preferred', 'discouraged']).optional(),
})
    .meta({ id: 'PasskeyAuthenticationOptions' });
/** `RegistrationResponseJSON` que entrega el navegador tras crear la passkey. */
export const PasskeyRegistrationResponseSchema = z
    .looseObject({
    id: Base64UrlSchema,
    rawId: Base64UrlSchema,
    type: z.literal('public-key'),
    response: z.looseObject({
        clientDataJSON: Base64UrlSchema,
        attestationObject: Base64UrlSchema,
        transports: z.array(AuthenticatorTransportSchema).optional(),
    }),
    authenticatorAttachment: z.string().optional(),
    clientExtensionResults: z.record(z.string(), z.unknown()),
})
    .meta({ id: 'PasskeyRegistrationResponse' });
/** `AuthenticationResponseJSON` (aserción firmada por el autenticador del dispositivo). */
export const PasskeyAssertionSchema = z
    .looseObject({
    id: Base64UrlSchema,
    rawId: Base64UrlSchema,
    type: z.literal('public-key'),
    response: z.looseObject({
        clientDataJSON: Base64UrlSchema,
        authenticatorData: Base64UrlSchema,
        signature: Base64UrlSchema,
        userHandle: Base64UrlSchema.optional(),
    }),
    authenticatorAttachment: z.string().optional(),
    clientExtensionResults: z.record(z.string(), z.unknown()),
})
    .meta({ id: 'PasskeyAssertion' });
/**
 * Registrar una passkey exige un paso adicional reciente: `scaChallengeId` de un reto OTP del mismo
 * cliente verificado hace poco (`PASSKEY_ENROLLMENT_WINDOW_SECONDS`) y sin passkey registrada con él.
 */
export const PASSKEY_ENROLLMENT_WINDOW_SECONDS = 10 * 60;
export const PasskeyRegistrationOptionsRequestSchema = z
    .object({ scaChallengeId: ScaChallengeIdSchema })
    .meta({ id: 'PasskeyRegistrationOptionsRequest' });
export const RegisterPasskeyRequestSchema = z
    .object({
    scaChallengeId: ScaChallengeIdSchema,
    registration: PasskeyRegistrationResponseSchema,
    /** Nombre visible del dispositivo ("iPhone", "Android"…), sin datos personales. */
    deviceLabel: z.string().trim().min(1).max(60).optional(),
})
    .meta({ id: 'RegisterPasskeyRequest' });
export const PasskeySchema = z
    .object({
    passkeyId: PasskeyIdSchema,
    /** Id de la credencial WebAuthn (base64url): el dispositivo lo recuerda para ofrecer biometría. */
    credentialId: Base64UrlSchema,
    deviceLabel: z.string().nullable(),
    createdAt: TimestampSchema,
    lastUsedAt: TimestampSchema.nullable(),
})
    .meta({ id: 'Passkey' });
export const PasskeyListSchema = z
    .object({ items: z.array(PasskeySchema) })
    .meta({ id: 'PasskeyList' });
export const VerifyScaRequestSchema = z
    .object({
    code: z
        .string()
        .regex(/^\d{6}$/)
        .optional(),
    passkeyAssertion: PasskeyAssertionSchema.optional(),
})
    .refine((v) => (v.code === undefined) !== (v.passkeyAssertion === undefined), {
    message: 'Envía `code` o `passkeyAssertion`',
})
    .meta({ id: 'VerifyScaRequest' });
/** El token SCA es válido solo para `operationId` + monto + beneficiario, un uso, 5 min. */
export const ScaBindingSchema = z.object({
    operationType: ScaOperationTypeSchema,
    operationId: z.string(),
    amount: MoneySchema.optional(),
    payeeHash: z.string().optional(),
});
export const VerifyScaResponseSchema = z
    .object({
    scaToken: z.string(),
    boundTo: z.object({ operationId: z.string(), amount: z.string().optional() }),
    expiresAt: TimestampSchema,
})
    .meta({ id: 'VerifyScaResponse' });
export const DelegatedScaResultRequestSchema = z.object({
    result: z.enum(['APPROVED', 'DENIED']),
    occurredAt: TimestampSchema,
});
//# sourceMappingURL=auth.js.map